import { useEffect, useRef } from 'react';
import { getSocket } from '../../../helpers/socket';
import { apiFetch } from '../shared/api';

export function useScorecardLive({ demo, endpoints, polling, onScorecard }) {
    const onScorecardRef = useRef(onScorecard);

    useEffect(() => {
        onScorecardRef.current = onScorecard;
    }, [onScorecard]);

    useEffect(() => {
        const socket = getSocket();
        if (!socket) {
            return undefined;
        }

        const subscribe = () => socket.emit('subscribe', demo.realtime_channel);
        const handleConnect = () => subscribe();
        const handleUpdated = (payload) => {
            // rtf-websocket server.mjs (the Redis pmessage handler) relays the
            // parsed Laravel envelope {event, data, socket} unchanged - unwrap it.
            const data = payload && typeof payload === 'object' && payload.data ? payload.data : payload;
            if (!data || Number(data.tellmyco_id) !== Number(demo.id)) {
                return;
            }
            onScorecardRef.current({
                status: data.status,
                url: data.url,
                scorecard_id: data.scorecard_id,
            });
        };

        subscribe();
        socket.on('connect', handleConnect);
        socket.on('DemoScorecardUpdated', handleUpdated);

        return () => {
            socket.off('connect', handleConnect);
            socket.off('DemoScorecardUpdated', handleUpdated);
        };
    }, [demo.id, demo.realtime_channel]);

    useEffect(() => {
        if (demo.scorecard.status !== 'requested') {
            return undefined;
        }

        const socket = getSocket();
        const startedAt = Date.now();
        let cancelled = false;
        let timer = null;

        // Recursive setTimeout (not setInterval) so the maxMs cutoff is checked
        // before each fetch, never firing one past the deadline.
        const tick = async () => {
            if (cancelled) {
                return;
            }
            if (Date.now() - startedAt > polling.maxMs) {
                return;
            }
            const { json } = await apiFetch(endpoints.scorecard);
            if (cancelled) {
                return;
            }
            if (json && json.data) {
                onScorecardRef.current(json.data);
            }
            timer = setTimeout(tick, polling.intervalMs);
        };

        const firstDelay = socket?.connected ? polling.firstDelayMs : polling.intervalMs;
        timer = setTimeout(tick, firstDelay);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [demo.id, demo.scorecard.status, endpoints.scorecard, polling.firstDelayMs, polling.intervalMs, polling.maxMs]);
}
